/**
 * Der eigene Zugang: Passwort ändern.
 *
 * Warum dieser Bildschirm gleich zu Anfang dabei ist und nicht „später":
 * Jedes Bürokonto bekommt sein erstes Passwort von jemand anderem — beim
 * Anlegen oder nach einem Zurücksetzen. Ein Passwort, das man nicht ändern
 * kann, bleibt genau so stehen, wie es einmal am Telefon durchgegeben wurde,
 * und wandert im Betrieb dann auf einen Zettel.
 *
 * Deshalb gibt es diese Seite in zwei Ausführungen. Normal steht sie in der
 * Navigation. Ist am Konto vermerkt, dass das Passwort gewechselt werden muss,
 * steht sie ALLEIN da — ohne Navigation, ohne Weg daran vorbei. Ein Hinweis,
 * den man wegklicken kann, wird weggeklickt.
 *
 * Das aktuelle Passwort wird abgefragt, obwohl Supabase das nicht verlangt.
 * Grund: Ein Rechner im Büro steht selten alleine. Wer sich kurz an einen
 * offenen Bildschirm setzt, soll nicht mit zwei Klicks das Konto übernehmen
 * können. Geprüft wird es, indem wir uns damit still neu anmelden — schlägt
 * das fehl, war es das falsche.
 */

import { useState, type ReactNode } from 'react';
import { Seitenkopf } from '../Rahmen';
import { Karte, Knopf, Meldung, Textfeld } from '../gestaltung/bausteine';
import { supabase } from '../supabase';
import { useSitzung } from '../anmeldung/Sitzung';

const MINDESTLAENGE = 10;

export function ZugangSeite(eigenschaften: { erzwungen?: boolean } = {}) {
  const { stand, abmelden } = useSitzung();
  const email = stand.art === 'bereit' ? stand.email : null;

  const [alt, setAlt] = useState('');
  const [neu, setNeu] = useState('');
  const [nochmal, setNochmal] = useState('');
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [fertig, setFertig] = useState(false);

  const zuKurz = neu.length > 0 && neu.length < MINDESTLAENGE;
  const passtNicht = nochmal.length > 0 && neu !== nochmal;
  const bereit =
    alt.length > 0 && neu.length >= MINDESTLAENGE && neu === nochmal && !laeuft;

  async function aendern() {
    if (!bereit || !email) return;
    setLaeuft(true);
    setFehler(null);
    setFertig(false);

    // Schritt 1: Stimmt das alte Passwort? Die Anmeldung erneuert dabei nur
    // die eigene Sitzung — es geht nichts verloren, wenn sie klappt.
    const pruefung = await supabase.auth.signInWithPassword({
      email,
      password: alt,
    });
    if (pruefung.error) {
      setFehler('Das aktuelle Passwort stimmt nicht.');
      setLaeuft(false);
      return;
    }

    // Der Vermerk wird im selben Zug gelöscht. Getrennt zu speichern hiesse:
    // wenn der zweite Aufruf scheitert, hängt jemand mit neuem Passwort in der
    // Wechselaufforderung fest.
    const { error } = await supabase.auth.updateUser({
      password: neu,
      data: { passwort_wechseln: false },
    });
    if (error) {
      setFehler(
        error.message.includes('should be different')
          ? 'Das neue Passwort ist dasselbe wie das alte.'
          : error.message,
      );
      setLaeuft(false);
      return;
    }

    setAlt('');
    setNeu('');
    setNochmal('');
    setFertig(true);
    setLaeuft(false);
  }

  const formular = (
    <div className="stapel" style={{ maxWidth: 420 }}>
      {fertig && (
        <Meldung art="gut" titel="Passwort geändert">
          Ab sofort gilt das neue Passwort. Andere angemeldete Browser bleiben
          angemeldet, bis ihre Sitzung abläuft.
        </Meldung>
      )}
      {fehler && <Meldung art="fehler">{fehler}</Meldung>}

      <Textfeld
        marke="Aktuelles Passwort"
        wert={alt}
        setzen={setAlt}
        art="password"
        autoComplete="current-password"
        disabled={laeuft}
        autoFocus={eigenschaften.erzwungen}
        hinweis={
          eigenschaften.erzwungen
            ? 'Das Einmalpasswort, das du bekommen hast.'
            : undefined
        }
      />
      <Textfeld
        marke="Neues Passwort"
        wert={neu}
        setzen={setNeu}
        art="password"
        autoComplete="new-password"
        disabled={laeuft}
        fehler={zuKurz ? `Mindestens ${MINDESTLAENGE} Zeichen.` : null}
        hinweis={`Mindestens ${MINDESTLAENGE} Zeichen. Länger ist besser als kompliziert — drei Wörter schlagen ein kurzes Kauderwelsch.`}
      />
      <Textfeld
        marke="Neues Passwort wiederholen"
        wert={nochmal}
        setzen={setNochmal}
        art="password"
        autoComplete="new-password"
        disabled={laeuft}
        fehler={passtNicht ? 'Die beiden stimmen nicht überein.' : null}
      />

      <div className="reihe reihe--rechts">
        <Knopf art="haupt" disabled={!bereit} onClick={() => void aendern()}>
          {laeuft ? 'Wird geändert …' : 'Passwort ändern'}
        </Knopf>
      </div>
    </div>
  );

  if (eigenschaften.erzwungen) {
    return (
      <Erzwungen abmelden={() => void abmelden()} email={email}>
        {formular}
      </Erzwungen>
    );
  }

  return (
    <>
      <Seitenkopf
        titel="Mein Zugang"
        erklaerung={
          email
            ? `Angemeldet als ${email}.`
            : 'Angemeldet — die E-Mail-Adresse konnte nicht gelesen werden.'
        }
      />
      <Karte titel="Passwort ändern">{formular}</Karte>
    </>
  );
}

/**
 * Die Ausführung ohne Ausweg.
 *
 * Bewusst mit Abmeldeknopf: Wer merkt, dass er am falschen Konto sitzt, muss
 * hier wieder rauskommen, ohne ein Passwort zu setzen.
 */
function Erzwungen(eigenschaften: {
  children: ReactNode;
  email: string | null;
  abmelden: () => void;
}) {
  return (
    <div className="anmelden">
      <div className="anmelden__karte" style={{ maxWidth: 460 }}>
        <div className="anmelden__marke">
          <span>Haid &amp; Jerg</span>
          <h1>Passwort setzen</h1>
        </div>

        <Meldung art="warnung" titel="Dein Passwort wurde von jemand anderem vergeben">
          Bevor es weitergeht, vergib bitte ein eigenes. Das Einmalpasswort
          kennt noch jemand — bei einem Passwort ist das einer zu viel.
        </Meldung>

        {eigenschaften.children}

        <div className="reihe reihe--rechts">
          <Knopf art="still" onClick={eigenschaften.abmelden}>
            Falsches Konto? Abmelden
          </Knopf>
        </div>

        {eigenschaften.email && (
          <p className="meta">Angemeldet als {eigenschaften.email}</p>
        )}
      </div>
    </div>
  );
}
